import { difference, groupBy, keyBy, mapValues, partition, sum, uniqWith } from 'es-toolkit';

import {
  Component,
  ComponentDesign,
  getLumberDescription,
  isTruss,
  Lumber,
  Member,
  MemberType,
  PlateGeometryType,
  PlateType,
  Truss,
} from '@paragon/contract-library';
import {
  angleEquals,
  Direction3D,
  distanceEquals,
  IsometricEmbedding,
  Line2D,
  Line3D,
  LineSegment3D,
  Point2D,
  point2DEquals,
  Point3D,
  Polygon2D,
  Polygon3D,
  supplementalAngleDegrees,
  Vector2D,
} from '@paragon/geometry-library';
import { ComponentMaterialStockLengths, getPlateTypeAndSizeString, lumbersAreEqual } from '@paragon/material-lists';
import { calculateMemberMidline, calculateMemberOverallLength, calculateMemberPolygon } from '@paragon/three-shared';
import { groupByWithoutNullishKeys, sortBy } from '@paragon/utilities';

/**
 * This represents members of the same cut (potentially different lengths)
 */
export interface AssemblyMemberPart {
  /** A unique identifier used as a React key*/
  readonly guid: string;

  /** This represents the member cuts for members of potentially different lengths */
  readonly getStandardizedMemberPolygonAtOrigin: (
    standardizedMemberWidth: number,
    standardizeMemberLength: number,
  ) => Polygon2D;

  /** The member cuts that define this `AssemblyPart` */
  readonly memberCuts: MemberCuts;

  /** Entries for each length */
  readonly membersOfCutAndLength: MembersOfCutAndLength[];
}

export interface MembersOfCutAndLength {
  readonly length: number;
  readonly stockLength: number | null;
  readonly componentInfo: AssemblyMemberPartComponentInfo[];
}

export interface MemberCuts {
  /** In degrees */
  readonly leftCutAnglesBottomToTop: readonly number[];
  readonly leftCutDistancesBottomToTopInInches: readonly number[];
  /** In degrees */
  readonly rightCutAnglesBottomToTop: readonly number[];
  readonly rightCutDistancesBottomToTopInInches: readonly number[];
  readonly lumber: Lumber;
  // TODO Add bevel cuts
}

export interface AssemblyMemberPartComponentInfo {
  readonly componentName: string;
  readonly memberType: MemberType;
  /** The quantity accounts for multiple plies */
  readonly quantity: number;
  /** The members matching the `AssemblyPart` */
  readonly members: readonly Member[];
  readonly component: Component;
}

export interface AssemblyPlatePart {
  readonly width: number;
  readonly length: number;
  readonly geometryType: PlateGeometryType;
  readonly plateType: PlateType;
  readonly tangiblePlateTypesForComboPlateType: readonly PlateType[];
  readonly quantityPerPly?: number;
  readonly quantityPerTruss?: number;
  readonly overallQuantity: number;
}

export function chordsFilter(members: readonly Member[]) {
  return members.filter((member) => member.memberType === 'TopChord' || member.memberType === 'BottomChord');
}

export function websFilter(members: readonly Member[], component: Component) {
  return members.filter((member) => member.memberType === 'Web' && !memberHasOnlyRightAngleCuts(member, component));
}

export function blocksFilter(members: readonly Member[], component: Component) {
  return members.filter(
    (member) =>
      ['Web', 'EndVertical', 'RibbonBlock'].includes(member.memberType) &&
      memberHasOnlyRightAngleCuts(member, component),
  );
}

function memberHasOnlyRightAngleCuts(member: Member, component: Component) {
  const memberPolygon = calculateMemberPolygon(member, component);
  const memberMidline = calculateMemberMidline(member, component);
  if (memberPolygon === null || memberMidline === null) throw new Error('Member polygon or midline not found');

  const memberMidlineAsLine = Line2D.fromPoints(
    Point2D.fromObject(memberMidline.start),
    Point2D.fromObject(memberMidline.end),
  );
  const edgesNotParallelToMidline = memberPolygon.edges.filter(
    (edge) => !edge.direction.isParallelTo(memberMidlineAsLine.direction),
  );
  return edgesNotParallelToMidline.every((cutEdge) => cutEdge.isPerpendicularTo(memberMidlineAsLine));
}

/**
 * Return the assembly parts in the order: chords, webs, blocks, other
 */
function sortAssemblyMemberParts(
  assemblyMemberParts: AssemblyMemberPart[],
  sortOrder: 'asc' | 'desc',
): readonly AssemblyMemberPart[] {
  const assemblyMemberPartsWithSortedMembersOfCutAndLength = assemblyMemberParts.map((assemblyMemberPart) => ({
    ...assemblyMemberPart,
    membersOfCutAndLength: sortMembersOfCutAndLength(assemblyMemberPart.membersOfCutAndLength),
  }));

  const componentInfoByAssemblyPartGuid = mapValues(
    keyBy(assemblyMemberPartsWithSortedMembersOfCutAndLength, (assemblyPart) => assemblyPart.guid),
    (assemblyPart) =>
      assemblyPart.membersOfCutAndLength.flatMap((membersOfCutAndLength) => membersOfCutAndLength.componentInfo),
  );
  const chordParts = assemblyMemberPartsWithSortedMembersOfCutAndLength.filter((assemblyPart) =>
    componentInfoByAssemblyPartGuid[assemblyPart.guid].some(
      (componentInfo) => chordsFilter(componentInfo.members).length > 0,
    ),
  );
  // It is possible a "part" (i.e., member) could be a chord in one truss and a different member-type in another truss,
  // which is why we filter on remaining parts to avoid duplicate entries. A part will be classified by the first
  // category it qualifies for (chord, web, block, then other). Users have a way to filter to more detailed information.
  let remainingParts = difference(assemblyMemberPartsWithSortedMembersOfCutAndLength, chordParts);
  const webParts = remainingParts.filter((assemblyPart) =>
    componentInfoByAssemblyPartGuid[assemblyPart.guid].some(
      (componentInfo) => websFilter(componentInfo.members, componentInfo.component).length > 0,
    ),
  );
  remainingParts = difference(remainingParts, webParts);
  const blockParts = remainingParts.filter((assemblyPart) =>
    componentInfoByAssemblyPartGuid[assemblyPart.guid].some(
      (componentInfo) => blocksFilter(componentInfo.members, componentInfo.component).length > 0,
    ),
  );
  const otherParts = difference(remainingParts, blockParts);

  return sortByQuantity(chordParts, sortOrder)
    .concat(sortByQuantity(webParts, sortOrder))
    .concat(sortByQuantity(blockParts, sortOrder))
    .concat(sortByQuantity(otherParts, sortOrder));
}

function sortByQuantity(
  assemblyMemberParts: AssemblyMemberPart[],
  sortOrder: 'asc' | 'desc',
): readonly AssemblyMemberPart[] {
  return sortBy(
    assemblyMemberParts,
    [
      (assemblyMemberPart) =>
        sum(
          assemblyMemberPart.membersOfCutAndLength.flatMap((membersOfCutAndLength) =>
            membersOfCutAndLength.componentInfo.map((componentInfo) => componentInfo.quantity),
          ),
        ),
      (assemblyPart) => getLumberDescription(assemblyPart.memberCuts.lumber),
      (assemblyPart) => assemblyPart.membersOfCutAndLength[0].length,
    ],
    [sortOrder, 'asc', 'asc'],
  );
}

function sortMembersOfCutAndLength(membersOfCutAndLength: readonly MembersOfCutAndLength[]) {
  return sortBy(membersOfCutAndLength, (memberOfCutAndLength) => memberOfCutAndLength.length);
}

function translatePolygonToOrigin(polygon: Polygon2D): [translatedPolygon: Polygon2D, translation: Vector2D] {
  const { vertices } = polygon;
  const leftmostX = Math.min(...vertices.map((vertex) => vertex.x));
  const lowestY = Math.min(...vertices.map((vertex) => vertex.y));
  const translation = new Vector2D(leftmostX * -1, lowestY * -1);
  const translatedPolygon = polygon.translate(translation);
  return [translatedPolygon, translation];
}

function translateMemberToOrigin(
  member: Member,
  component: Component,
): [memberPolygonAtOrigin: Polygon2D, memberMidlineAtOrigin: Line2D] | null {
  const memberPolygon = calculateMemberPolygon(member, component);
  const memberMidline = calculateMemberMidline(member, component);
  if (!memberPolygon || !memberMidline) return null;
  const memberMidlineLineSegment = new LineSegment3D(
    Point3D.fromObject(memberMidline.start),
    Point3D.fromObject(memberMidline.end),
  );
  // IsometricEmbedding serves our purposes here, but is not directly intended for this purpose. In the future,
  // there may be a more fitting alternative to use here. The goal is to get the member polygon to be at the origin so
  // that we can compare it with other member polygons to determine if they are the same.
  const isometricEmbedding = new IsometricEmbedding(
    memberMidlineLineSegment.direction,
    Direction3D.out.crossProductDirection(memberMidlineLineSegment.direction)!,
    memberPolygon.centroid.to3D(),
  );
  const memberPolygonWithCentroidAtOrigin = isometricEmbedding.polygonTo2D(
    new Polygon3D(memberPolygon.vertices.map((vertex) => vertex.to3D())),
  );
  const [memberPolygonAtOrigin, translation] = translatePolygonToOrigin(memberPolygonWithCentroidAtOrigin);
  const memberMidlineLine = Line3D.fromPoints(
    new Point3D(memberMidline.start.x, memberMidline.start.y, 0),
    new Point3D(memberMidline.end.x, memberMidline.end.y, 0),
  )!;
  const memberMidlineForMemberPolygonAtOrigin = isometricEmbedding.lineTo2D(memberMidlineLine).translate(translation);

  return [memberPolygonAtOrigin, memberMidlineForMemberPolygonAtOrigin];
}

/**
 * Because members can vary substantially in length, this function standardizes their length so that they have a
 * consistent visibility, much like is done on printed shop drawings.
 */
export function standardizeMemberPolygonAtOriginForDiagram(memberPolygonAtOrigin: Polygon2D) {
  return function standardizeMemberPolygonAtOriginForDiagramWithWidthAndLength(
    standardizedMemberWidth: number,
    standardizedMemberLength: number,
  ) {
    const { vertices } = memberPolygonAtOrigin;
    const [leftVertices, rightVertices] = partition(vertices, (vertex) => vertex.x < memberPolygonAtOrigin.centroid.x);
    const leftCutLength =
      Math.max(...leftVertices.map((vertex) => vertex.x)) - Math.min(...leftVertices.map((vertex) => vertex.x));
    const rightCutLength =
      Math.max(...rightVertices.map((vertex) => vertex.x)) - Math.min(...rightVertices.map((vertex) => vertex.x));
    const memberCutLength = leftCutLength + rightCutLength;
    const lengthBoundScaleFactor = standardizedMemberLength / memberCutLength;

    const lowestY = Math.min(...vertices.map((vertex) => vertex.y));
    const highestY = Math.max(...vertices.map((vertex) => vertex.y));
    const currentWidth = highestY - lowestY;
    const widthBoundScaleFactor = standardizedMemberWidth / currentWidth;

    const scaleFactor = Math.min(lengthBoundScaleFactor, widthBoundScaleFactor);
    const scaledVertices = vertices.map((vertex) => vertex.multiplyScalar(scaleFactor));
    const scaledPolygon = new Polygon2D(scaledVertices);
    const [scaledPolygonAtOrigin] = translatePolygonToOrigin(scaledPolygon);

    const [scaledLeftVertices, scaledRightVertices] = partition(
      scaledPolygonAtOrigin.vertices,
      (vertex) => vertex.x < scaledPolygonAtOrigin.centroid.x,
    );
    const rightmostX = Math.max(...scaledRightVertices.map((vertex) => vertex.x));
    const xTranslation = (rightmostX - standardizedMemberLength) * -1;
    const rightVerticesAtStandardizedLength = scaledRightVertices.map(
      (vertex) => new Point2D(vertex.x + xTranslation, vertex.y),
    );
    return new Polygon2D(
      // uniqWith because for length bound standardized members, the first/last or middle two points may be the same,
      // which leads to a line segment has no breadth error.
      uniqWith(
        sortBy(scaledLeftVertices, (vertex) => vertex.y, 'asc').concat(
          sortBy(rightVerticesAtStandardizedLength, (vertex) => vertex.y, 'desc'),
        ),
        point2DEquals,
      ),
    );
  };
}

function membersToComponentInfoList(members: readonly Member[], truss: Truss, componentQuantity: number) {
  const componentInfo: AssemblyMemberPartComponentInfo[] = [];
  const membersGroupedByMemberType = groupBy(members, (member) => member.memberType);
  for (const [memberType, membersOfMemberType] of Object.entries(membersGroupedByMemberType)) {
    componentInfo.push({
      componentName: truss.name,
      memberType: memberType as MemberType, // Type was lost in the groupBy
      quantity: membersOfMemberType.length * componentQuantity * truss.numberOfPlies,
      members: membersOfMemberType,
      component: truss,
    });
  }
  return componentInfo;
}

// The identifying GUID could be either a ComponentDesign GUID or an AnalysisSet GUID, depending on the context, but
// must be consistent between the two parameters.
export function getAssemblyMemberParts(
  quantitiesByIdentifyingGuid: { [identifyingGuid: string]: number },
  componentDesignsByIdentifyingGuid: { [identifyingGuid: string]: ComponentDesign },
  stockLengths: ComponentMaterialStockLengths | null,
  sortOrder: 'asc' | 'desc',
) {
  // Loop over the analysis sets to determine the assembly parts
  const assemblyParts: AssemblyMemberPart[] = [];
  for (const [identifyingGuid, quantity] of Object.entries(quantitiesByIdentifyingGuid)) {
    const componentDesign = componentDesignsByIdentifyingGuid[identifyingGuid];
    if (!componentDesign) continue;
    const { component } = componentDesign;
    if (!isTruss(component)) throw new Error('Component is not a truss');

    // Group members with matching assembly names because they have the same geometry
    const { members } = component;
    const membersGroupedByAssemblyName = groupByWithoutNullishKeys(members, (member) => member.assemblyName);
    for (const membersOfAssemblyName of Object.values(membersGroupedByAssemblyName)) {
      const representativeMember = membersOfAssemblyName[0];
      const memberPolygonAndMidlineAtOrigin = translateMemberToOrigin(representativeMember, component);
      if (!memberPolygonAndMidlineAtOrigin) {
        console.error('Could not get member polygon');
        continue;
      }
      const [memberPolygonAtOrigin, memberMidlineAtOrigin] = memberPolygonAndMidlineAtOrigin;
      const edgesNotParallelToMidline = memberPolygonAtOrigin.edges.filter(
        (edge) => !edge.direction.isParallelTo(memberMidlineAtOrigin.direction),
      );
      const [leftEdgesNotParallelToMidline, rightEdgesNotParallelToMidline] = partition(
        edgesNotParallelToMidline,
        (edgeNotParallelToMidline) => edgeNotParallelToMidline.midpoint.x < memberPolygonAtOrigin.centroid.x,
      );
      const leftEdgesNotParallelToMidlineBottomToTop = sortBy(leftEdgesNotParallelToMidline, (edge) => edge.midpoint.y);
      const leftAnglesBottomToTop = leftEdgesNotParallelToMidlineBottomToTop.map((leftEdge) =>
        leftEdge.direction.to3D()!.smallestAngleBetween(Direction3D.right),
      );
      const leftCutDistancesBottomToTop = leftEdgesNotParallelToMidlineBottomToTop.map((leftEdge) =>
        Math.abs(leftEdge.endPoint.y - leftEdge.basePoint.y),
      );
      const rightEdgesNotParallelToMidlineBottomToTop = sortBy(
        rightEdgesNotParallelToMidline,
        (edge) => edge.midpoint.y,
      );
      const rightAnglesBottomToTop = rightEdgesNotParallelToMidlineBottomToTop.map((rightEdge) =>
        rightEdge.direction.to3D()!.smallestAngleBetween(Direction3D.right),
      );
      const rightCutDistancesBottomToTop = leftEdgesNotParallelToMidlineBottomToTop.map((rightEdge) =>
        Math.abs(rightEdge.endPoint.y - rightEdge.basePoint.y),
      );
      const memberCuts: MemberCuts = {
        leftCutAnglesBottomToTop: leftAnglesBottomToTop,
        leftCutDistancesBottomToTopInInches: leftCutDistancesBottomToTop,
        rightCutAnglesBottomToTop: rightAnglesBottomToTop,
        rightCutDistancesBottomToTopInInches: rightCutDistancesBottomToTop,
        lumber: representativeMember.lumber,
      };

      const length = calculateMemberOverallLength(representativeMember, component);
      if (length === null) throw new Error('Member length not available');
      const stockLengthsForLumber = stockLengths?.getStockLengths(componentDesign, representativeMember.lumber) ?? null;
      const stockLength =
        stockLengthsForLumber === null
          ? null
          : Math.min(
              ...stockLengthsForLumber.filter(
                (stockLengthForLumber) => stockLengthForLumber > length || distanceEquals(stockLengthForLumber, length),
              ),
            );
      const componentInfo = membersToComponentInfoList(membersOfAssemblyName, component, quantity);

      // See if the member polygon matches any existing member polygons
      // If so, tack on information to that existing `AssemblyPart`
      let matchesExistingAssemblyPart = false;
      for (const assemblyPart of assemblyParts) {
        // Find if matching cuts exist. If they do, add to that entry.
        if (memberCutsAreEqual(memberCuts, assemblyPart.memberCuts)) {
          matchesExistingAssemblyPart = true;

          // Find if an entry with matching length exists. If so, add to that entry.
          const existingMembersOfCutAndLength = assemblyPart.membersOfCutAndLength.find((membersOfCutAndLength) =>
            distanceEquals(membersOfCutAndLength.length, length),
          );
          if (existingMembersOfCutAndLength === undefined) {
            const membersOfCutAndLength: MembersOfCutAndLength = { length, stockLength, componentInfo };
            assemblyPart.membersOfCutAndLength.push(membersOfCutAndLength);
          } else {
            existingMembersOfCutAndLength.componentInfo.push(...componentInfo);
          }
        }
      }

      // If no match is found, add a new `AssemblyPart`
      if (!matchesExistingAssemblyPart) {
        assemblyParts.push({
          guid: crypto.randomUUID(),
          getStandardizedMemberPolygonAtOrigin: standardizeMemberPolygonAtOriginForDiagram(memberPolygonAtOrigin),
          memberCuts,
          membersOfCutAndLength: [{ length, stockLength, componentInfo }],
        });
      }
    }
  }
  return sortAssemblyMemberParts(assemblyParts, sortOrder);
}

function memberCutsAreEqual(memberCutsOne: MemberCuts, memberCutsTwo: MemberCuts): boolean {
  return (
    (areSameCuts(memberCutsOne, memberCutsTwo) ||
      areSameCutsRotated180(memberCutsOne, memberCutsTwo) ||
      areSameCutsMirroredAcrossVerticalCenterLine(memberCutsOne, memberCutsTwo) ||
      areSameCutsMirroredAcrossHorizontalCenterLine(memberCutsOne, memberCutsTwo)) &&
    lumbersAreEqual(memberCutsOne.lumber, memberCutsTwo.lumber)
  );
}

function areSameCuts(memberCutsOne: MemberCuts, memberCutsTwo: MemberCuts): boolean {
  return (
    angleSequenceEqual(memberCutsOne.leftCutAnglesBottomToTop, memberCutsTwo.leftCutAnglesBottomToTop) &&
    angleSequenceEqual(memberCutsOne.rightCutAnglesBottomToTop, memberCutsTwo.rightCutAnglesBottomToTop)
  );
}

function areSameCutsRotated180(memberCutsOne: MemberCuts, memberCutsTwo: MemberCuts): boolean {
  return (
    angleSequenceEqual(
      memberCutsOne.leftCutAnglesBottomToTop,
      memberCutsTwo.rightCutAnglesBottomToTop.map((angle) => supplementalAngleDegrees(angle)).toReversed(),
    ) &&
    angleSequenceEqual(
      memberCutsOne.rightCutAnglesBottomToTop,
      memberCutsTwo.leftCutAnglesBottomToTop.map((angle) => supplementalAngleDegrees(angle)).toReversed(),
    )
  );
}

function areSameCutsMirroredAcrossVerticalCenterLine(memberCutsOne: MemberCuts, memberCutsTwo: MemberCuts): boolean {
  return (
    angleSequenceEqual(memberCutsOne.leftCutAnglesBottomToTop, memberCutsTwo.rightCutAnglesBottomToTop) &&
    angleSequenceEqual(memberCutsOne.rightCutAnglesBottomToTop, memberCutsTwo.leftCutAnglesBottomToTop)
  );
}

function areSameCutsMirroredAcrossHorizontalCenterLine(memberCutsOne: MemberCuts, memberCutsTwo: MemberCuts): boolean {
  return (
    angleSequenceEqual(
      memberCutsOne.leftCutAnglesBottomToTop,
      memberCutsTwo.leftCutAnglesBottomToTop.map((angle) => supplementalAngleDegrees(angle)).toReversed(),
    ) &&
    angleSequenceEqual(
      memberCutsOne.rightCutAnglesBottomToTop,
      memberCutsTwo.rightCutAnglesBottomToTop.map((angle) => supplementalAngleDegrees(angle)).toReversed(),
    )
  );
}

function angleSequenceEqual(angleListOne: readonly number[], angleListTwo: readonly number[]): boolean {
  if (angleListOne.length !== angleListTwo.length) return false;
  for (let i = 0; i < angleListOne.length; i++) {
    if (!angleEquals(angleListOne[i], angleListTwo[i])) return false;
  }
  return true;
}

export function getAssemblyPlateParts(
  quantitiesByIdentifyingGuid: { [identifyingGuid: string]: number },
  componentDesignsByIdentifyingGuid: { [identifyingGuid: string]: ComponentDesign },
  recordTrussQuantity: boolean,
): readonly AssemblyPlatePart[] {
  const uniqueIdentifyingGuids = Object.keys(quantitiesByIdentifyingGuid);
  const identifyingGuidToTruss = uniqueIdentifyingGuids.reduce(
    (accumulator, analysisSetGuid) => {
      if (analysisSetGuid in componentDesignsByIdentifyingGuid) {
        const { component } = componentDesignsByIdentifyingGuid[analysisSetGuid];
        if (!isTruss(component)) throw new Error('Component is not a truss');
        accumulator[analysisSetGuid] = component;
      }
      return accumulator;
    },
    {} as { [analysisSetGuid: string]: Truss },
  );

  const assemblyPlatePartsByPlateTypeAndSize = Object.entries(identifyingGuidToTruss).reduce(
    (assemblyPlatePartsByPlateTypeAndSize, [analysisSetGuid, truss]) => {
      const { numberOfPlies } = truss;
      const platePairsGroupedByPlateTypeAndSize = groupBy(truss.platePairs, (platePair) =>
        getPlateTypeAndSizeString(platePair),
      );
      for (const [plateTypeAndSize, platePairsOfTypeAndSize] of Object.entries(platePairsGroupedByPlateTypeAndSize)) {
        const numberOfPlatesForOnePly = 2 * platePairsOfTypeAndSize.length; // Times 2 because a plate pair is two plates
        const numberOfPlatesOfTypeAndSizeForTruss = numberOfPlatesForOnePly * numberOfPlies;
        assemblyPlatePartsByPlateTypeAndSize[plateTypeAndSize] =
          plateTypeAndSize in assemblyPlatePartsByPlateTypeAndSize
            ? {
                ...assemblyPlatePartsByPlateTypeAndSize[plateTypeAndSize],
                overallQuantity:
                  assemblyPlatePartsByPlateTypeAndSize[plateTypeAndSize].overallQuantity +
                  numberOfPlatesOfTypeAndSizeForTruss * quantitiesByIdentifyingGuid[analysisSetGuid],
              }
            : {
                plateType: platePairsOfTypeAndSize[0].plateType,
                tangiblePlateTypesForComboPlateType: platePairsOfTypeAndSize[0].tangiblePlateTypesForComboPlateType,
                width: platePairsOfTypeAndSize[0].width,
                length: platePairsOfTypeAndSize[0].length,
                geometryType: platePairsOfTypeAndSize[0].geometryType,
                quantityPerPly: recordTrussQuantity ? numberOfPlatesForOnePly : undefined,
                quantityPerTruss: recordTrussQuantity ? numberOfPlatesOfTypeAndSizeForTruss : undefined,
                overallQuantity: numberOfPlatesOfTypeAndSizeForTruss * quantitiesByIdentifyingGuid[analysisSetGuid],
              };
      }
      return assemblyPlatePartsByPlateTypeAndSize;
    },
    {} as { [plateTypeAndSize: string]: AssemblyPlatePart },
  );
  return sortBy(Object.values(assemblyPlatePartsByPlateTypeAndSize), [
    (platePart) => platePart.plateType,
    (platePart) => platePart.width,
    (platePart) => platePart.length,
  ]);
}
